<template>
  <div>
    <v-toolbar flat>
      <v-toolbar-title>
        Lokationer
      </v-toolbar-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        solo
        hide-details
        dense
        clearable
        class="mr-3"
        append-icon="mdi-magnify"
      />
      <v-spacer />
      <CustomerLocationCreateDialog :customer-id="$route.params.customerId" @create:success="getLocations" />
    </v-toolbar>
    <v-data-table
      :items="locations"
      :headers="headers"
      :loading="loading.locations"
      :search="search"
      :items-per-page="15"
      :footer-props="{ 'items-per-page-options': [15, 30, 50, 75, -1] }"
    >
      <template #[`item.externalReferences`]="{ value }">
        <v-chip-group column>
          <v-chip v-for="ref in value" :key="ref" small label>
            {{ ref }}
          </v-chip>
        </v-chip-group>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn small @click="onEditItem(item)">
          <v-icon>mdi-pencil-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger
        </v-card-title>
        <v-card-text>
          <v-form
            v-if="editItem"
            ref="editForm"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              autofocus
              outlined
              :rules="[ v => !!v || 'Udfyld et navn' ]"
              dense
            />
            <v-text-field
              v-model="editItem.initials"
              label="Initialer"
              :rules="[ v => !!v || 'Udfyld initialer', v => String(v).length <= 5 || 'Maks. 5 tegn' ]"
              hint="Skal være unik for kunden"
              persistent-hint
              outlined
              dense
            />
            <v-autocomplete
              v-model="editItem.locationTypeId"
              :items="types"
              label="Type"
              item-value="id"
              item-text="title"
              :rules="[ v => !!v || 'Udfyld type' ]"
              clearable
              auto-select-first
              outlined
              dense
            />
            <v-autocomplete
              v-model="editItem.countryId"
              :items="countries"
              :loading="loading.countries"
              label="Land"
              item-value="id"
              item-text="name"
              :rules="[ v => !!v || 'Udfyld land' ]"
              clearable
              auto-select-first
              outlined
              dense
            />
            <v-text-field
              v-model="editItem.address"
              :label="$t('common.address')"
              outlined
              dense
            />
            <v-select
              v-model="editItem.locationAreaId"
              :items="areas"
              label="Område"
              item-text="name"
              item-value="id"
              outlined
              dense
              clearable
            />
            <v-combobox
              v-model="editItem.externalReferences"
              label="Ekstern reference"
              hint="Dette bliver f.eks. brugt til at matche automatisk oprettede opgaver med lokationen"
              persistent-hint
              outlined
              clearable
              multiple
              small-chips
              deletable-chips
              dense
            />
            <v-divider class="mt-2"></v-divider>
            <h4 class="my-3">
              Kontaktoplysninger
            </h4>
            <v-text-field
              v-model="editItem.contactName"
              label="Navn"
              outlined
              dense
            />
            <v-text-field
              v-model="editItem.contactEmail"
              label="E-mail"
              type="email"
              :rules="[rules.email]"
              outlined
              dense
            />
            <v-text-field
              v-model="editItem.contactPhone"
              label="Telefon"
              outlined
              dense
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />

          <v-btn :disabled="!editForm" :loading="saving" @click="saveItem()">
            {{ $t('common.save') }}
            <v-icon right> mdi-content-save-outline </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CustomerLocationCreateDialog from './CustomerLocationCreateDialog.vue';

export default {
  name: 'CustomerLocationsComponent',
  components: {
    CustomerLocationCreateDialog
  },
  props: {
    types: {
      type: Array,
      required: true
    },
    areas: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      creating: false,
      loading: {
        locations: false,
        countries: false
      },
      saving: false,
      countries: [],
      locations: [],
      addMenu: false,
      addForm: false,
      editDialog: false,
      editItem: null,
      editForm: false,
      search: '',
      headers: [
        { text: 'Navn', value: 'title', width: 300 },
        { text: 'Initialer', value: 'initials', width: 80, sortable: false },
        { text: 'Land', value: 'countryName', width: '15%' },
        { text: 'Type', value: 'typeName', width: '15%' },
        { text: 'Område', value: 'areaName', width: '15%' },
        { text: 'Ekstern ref.', value: 'externalReferences', width: '40%', sortable: false },
        { text: 'Actions', value: 'actions', align: 'right', sortable: false }
      ],
      rules: {
        email: value => (!value || !!value?.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) || 'Indtast en gyldig e-mail'
      }
    }
  },
  async mounted() {
    await this.getLocations();
    this.getCountries();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = null;
      }
    }
  },
  methods: {
    getLocations: async function() {
      this.loading.locations = true;

      try {
        const { data } = await this.$api.get('locations', {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.locations = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading.locations = false;
      }
    },
    getCountries: async function() {
      this.loading.countries = true;

      try {
        const { data } = await this.$api.get(`countries`);

        this.countries = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: 'Kunne ikke hente lande. Det er ikke muligt at oprette lokationer' });
      } finally {
        this.loading.countries = false;
      }
    },
    onEditItem: function(item) {      
      this.editItem = JSON.parse(JSON.stringify(item));      
      this.editDialog = true;
    },
    saveItem: async function() {      
      this.saving = true;

      const { title, initials, countryId, locationTypeId, locationAreaId, externalReferences, contactName, contactEmail, contactPhone } = this.editItem;

      const model = {
        title,
        initials,
        countryId,
        locationTypeId,
        locationAreaId,
        externalReferences,
        contactName,
        contactEmail,
        contactPhone
      };

      try {
        const { data } = await this.$api.patch(`locations/${this.editItem.id}`, model);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Lokation opdateret' });
        this.getLocations();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>